import { System } from './../shared-components/System';
import { forkJoin as observableForkJoin, Observable, of, Subject, BehaviorSubject } from 'rxjs';
import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { StudentService } from '../shared-components/student/student.service';
import { UserService } from '../shared-components/user/user.service';
import { YearService } from '../shared-components/year/year.service';
import { Portal } from '../shared-components/portal.model';
import { StudentDocumentsFilter } from './student-documents/student-documents-filter';
import { map } from 'rxjs/operators';
import { LoginService } from '../shared-modules/login/login.service';
import { GeneralConfirmationComponent } from '../shared-components/controls/general-confirmation/general-confirmation.component';
import { MatDialog } from '@angular/material/dialog';
import { Router } from '@angular/router';

@Injectable()
export class Student360Service {

    private _lastSelectedCalendarDate;
    private _lastSelectedCalenderView;
    public parentAssignedPortals: Portal[];
    public allParentPortals: Portal[];
    public academicYearAttention: Portal;
    public studentDocumentsFilterData: StudentDocumentsFilter;
    private observableParentAssignedPortals = new BehaviorSubject<Portal[]>([]);
    observableParentAssignedPortalsStream$ = this.observableParentAssignedPortals.asObservable();
    private observableAcademicYearAttention = new Subject<Portal>();
    observableAcademicYearAttentionStream$ = this.observableAcademicYearAttention.asObservable();

    constructor(
        private http: HttpClient,
        private studentService: StudentService,
        private userService: UserService,
        private yearService: YearService,
        private loginService: LoginService,
        private dialog: MatDialog,
        private router: Router
    ) {
        this.studentDocumentsFilterData = new StudentDocumentsFilter();

        // Getting new parent portals when a new year or student is selected
        const getNewParentPortals = () => {
            if (this.userService.userId != null && this.yearService.student360Year) {
                if (this.userService.userType != 2 || this.allParentPortals == undefined || !this.loginService.LoginSettings.V10) {
                    if (this.yearService.student360Year.Id > 0) {
                        this.parentAssignedPortals = [];
                        const subscription = this.getParentAssignedPortals().subscribe(portals => {
                            if (!portals) {
                                portals = [];
                            }
                            this.updateParentAssignedPortals(portals);
                            subscription.unsubscribe();
                        });
                    }
                }
                else {
                    this.updateParentAssignedPortals(this.allParentPortals);
                }
            }
        };
        this.yearService.observableStudent360YearStream$.subscribe(() => {
            if (this.userService.userType != null && this.userService.userType != 1 && this.studentService.selectedStudent) {
                getNewParentPortals();
            }
        });
        this.studentService.selectedStudentStream$.subscribe(() => {
            if (this.userService.userType != null && this.userService.userType != 1) {
                getNewParentPortals();
            }
        });
    }

    get lastSelectedCalendarDate(): any {
        return this._lastSelectedCalendarDate;
    }

    set lastSelectedCalendarDate(date: any) {
        this._lastSelectedCalendarDate = date;
    }

    get lastSelectedCalendarView(): string {
        return this._lastSelectedCalenderView;
    }

    set lastSelectedCalendarView(view: string) {
        this._lastSelectedCalenderView = view;
    }

    getParentAssignedPortals(): Observable<Portal[]> {
        if (this.parentAssignedPortals && this.parentAssignedPortals.length > 0) {
            return of(this.parentAssignedPortals);
        }
        if (!this.loginService.LoginSettings.V10) {
            const studentId = this.studentService.selectedStudent != null ? this.studentService.selectedStudent.Id : 0;
            return this.http.get<Portal[]>(System.apiURL.Student + 'GetParentAssignedPortals?parentid=' + this.userService.userId + '&studentid=' + studentId + '&yearid=' + this.yearService.student360Year.Id).pipe(
                map((portals) => {
                    // We need to separate the portals in multiple groups into separate portals because GroupName is a CSV
                    for (let i = 0; i < portals.length; i++) {
                        // All parent portals are marked as a parent page
                        portals[i].applyFilterStudents = true;

                        // If multiple groups are found, split the portals
                        if (portals[i].GroupName.includes(',')) {
                            const groups = portals[i].GroupName.split(',');
                            for (let j = 0; j < groups.length; j++) {
                                groups[j] = groups[j].trim();

                                // If this is the first element, alter our current portal
                                if (j == 0) {
                                    portals[i].GroupName = groups[0];

                                    // After the first element, create new portals
                                } else {
                                    const portalInGroup = new Portal();
                                    Object.assign(portalInGroup, portals[i]);
                                    portalInGroup.ShowAsTile = false;
                                    portalInGroup.GroupName = groups[j];
                                    portals.push(portalInGroup);
                                }
                            }
                        }
                        return Array.prototype.sort.call(portals, (a, b) => a.PortalName.localeCompare(b.PortalName));
                    }
                })
            );
        }
        else {
            return this.http.get<Portal[]>(System.apiURL.Student + 'GetParentStudentPortals?parentid=' + this.userService.userId + '&yearid=' + this.yearService.student360Year.Id).pipe(
                map((portals) => {
                    if (portals.length == 1 && portals[0].NoSchoolPolicy == true) {
                        // Open a modal letting the user know that there is no associated school policy
                        const dialogBox = this.dialog.open(GeneralConfirmationComponent, {
                            disableClose: true,
                            autoFocus: false,
                            data: {
                                headerLabel: 'No School Policy',
                                informational: true,
                                confirmMessage: 'We are unable to display your students information. Please contact the district and ask them to apply a school policy to your student\'s school.'
                            }
                        });
                        dialogBox.afterClosed().subscribe(() => {
                            this.router.navigateByUrl('/logout');
                        });
                    } else {
                        // We need to separate the portals in multiple groups into separate portals because GroupName is a CSV
                        for (let i = 0; i < portals.length; i++) {

                            // All parent portals are marked as a parent page
                            portals[i].applyFilterStudents = true;

                            // If multiple groups are found, split the portals
                            if (portals[i].GroupName.includes(',')) {
                                const groups = portals[i].GroupName.split(',');
                                for (let j = 0; j < groups.length; j++) {
                                    groups[j] = groups[j].trim();

                                    // If this is the first element, alter our current portal
                                    if (j == 0) {
                                        portals[i].GroupName = groups[0];

                                        // After the first element, create new portals
                                    } else {
                                        const portalInGroup = new Portal();
                                        Object.assign(portalInGroup, portals[i]);
                                        portalInGroup.ShowAsTile = false;
                                        portalInGroup.GroupName = groups[j];
                                        portals.push(portalInGroup);
                                    }
                                }
                            }
                        }
                        return Array.prototype.sort.call(portals, (a, b) => a.PortalName.localeCompare(b.PortalName));
                    }
                })
            );
        }
    }

    getStudentSummary(): Observable<any> {
        return observableForkJoin(
            this.http.get<any>(System.apiURL.Student + 'GetStudentSummaryChunk1?studentId=' + this.studentService.selectedStudent.Id + '&yearId=' + this.yearService.student360Year.Id + '&userId=' + this.userService.userId + '&userType=' + this.userService.userType),
            this.http.get<any>(System.apiURL.Student + 'GetStudentSummaryChunk2?studentId=' + this.studentService.selectedStudent.Id + '&yearId=' + this.yearService.student360Year.Id + '&userId=' + this.userService.userId + '&userType=' + this.userService.userType),
            (chunk1, chunk2) => ({ chunk1, chunk2 })
        );
    }

    updateParentAssignedPortals(portals: Portal[]) {
        if (this.loginService.LoginSettings.V10) {
            const parentPortals = portals.filter(p => p.Student_ID == this.studentService.selectedStudent.Id);
            this.allParentPortals = portals;
            this.parentAssignedPortals = parentPortals;
            this.observableParentAssignedPortals.next(parentPortals);
        }
        else {
            this.parentAssignedPortals = portals;
            this.allParentPortals = portals;
            this.observableParentAssignedPortals.next(portals);
        }
    }
    updateAcademicYearAttention(portal: Portal) {
        this.academicYearAttention = portal;
        this.observableAcademicYearAttention.next(this.academicYearAttention);
    }
}
